import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError, BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle2, XCircle, Award, RotateCcw, Lock, Download, ClipboardCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const BACKEND = BACKEND_URL;

function QuestionCard({ q, index, value, onChange, disabled, result }) {
  const multi = q.type === "multi";
  const selected = value || [];
  const toggle = (optId) => {
    if (disabled) return;
    if (multi) {
      onChange(selected.includes(optId) ? selected.filter((x) => x !== optId) : [...selected, optId]);
    } else {
      onChange([optId]);
    }
  };
  return (
    <div className="card-surface p-5" data-testid={`question-${q.id}`}>
      <div className="flex items-start justify-between gap-3">
        <h4 className="font-heading text-[16px] font-semibold leading-snug">
          {index + 1}. {q.text}
        </h4>
        {result && (
          result.is_correct
            ? <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-500" data-testid={`q-correct-${q.id}`} />
            : <XCircle className="h-5 w-5 shrink-0 text-red-500" data-testid={`q-wrong-${q.id}`} />
        )}
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        {multi ? "Select all that apply" : q.type === "tf" ? "True or false" : "Choose one"}
      </p>
      <div className="mt-3 space-y-2">
        {q.options.map((o) => {
          const isSel = selected.includes(o.id);
          const glyphSel = result ? (result.selected || []).includes(o.id) : isSel;
          const isCorrect = result?.correct_options?.includes(o.id);
          const wrongPick = result && (result.selected || []).includes(o.id) && !isCorrect;
          return (
            <button
              key={o.id}
              type="button"
              disabled={disabled}
              onClick={() => toggle(o.id)}
              data-testid={`option-${o.id}`}
              className={cn(
                "flex w-full items-center gap-3 rounded-lg border px-3.5 py-2.5 text-left text-sm transition-colors",
                isSel && !result && "border-[#33B6E4] bg-[#33B6E4]/8",
                !isSel && !result && "border-border hover:bg-accent",
                result && isCorrect && "border-emerald-400 bg-emerald-50 dark:bg-emerald-950/30",
                wrongPick && "border-red-400 bg-red-50 dark:bg-red-950/30",
                result && !isCorrect && !isSel && "border-border opacity-70",
                disabled && "cursor-default"
              )}
            >
              <span className={cn("grid h-4 w-4 shrink-0 place-items-center border",
                multi ? "rounded" : "rounded-full",
                glyphSel ? "border-[#33B6E4] bg-[#33B6E4]" : "border-muted-foreground/40")}>
                {glyphSel && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
              </span>
              <span className="flex-1">{o.text}</span>
              {result && isCorrect && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function AssessmentPanel({ courseId, onPassed }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState(null); // submit result

  const load = async () => {
    try {
      const { data } = await api.get(`/courses/${courseId}/assessment`);
      setData(data);
      setOutcome(null);
      setAnswers({});
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  if (!data) {
    return (
      <div className="card-surface p-6 text-center">
        <ClipboardCheck className="mx-auto mb-3 h-8 w-8 text-[#33B6E4]" />
        <h3 className="font-heading text-[18px] font-semibold">Final assessment</h3>
        <p className="mt-1 text-sm text-muted-foreground">Load the assessment to see the questions and your attempts.</p>
        <Button className="btn-brand mt-4" onClick={load} data-testid="load-assessment-button">Open assessment</Button>
      </div>
    );
  }

  const submit = async () => {
    const unanswered = data.questions.filter((q) => !(answers[q.id]?.length));
    if (unanswered.length) return toast.error(`Answer all ${data.questions.length} questions first`);
    setSubmitting(true);
    try {
      const { data: res } = await api.post(`/courses/${courseId}/assessment/submit`, { answers });
      setOutcome(res);
      if (res.passed) {
        toast.success(`Passed with ${res.score}%. CPD awarded`);
        onPassed?.();
      } else {
        toast.error(`Scored ${res.score}%. The pass mark is ${res.pass_mark}%`);
      }
      // Keep the outcome/answers on screen for review; just refresh metadata locally.
      setData((d) => d ? {
        ...d,
        attempts_used: d.attempts_used + 1,
        attempts_remaining: res.attempts_remaining,
        passed: res.passed || d.passed,
        best_score: Math.max(d.best_score ?? 0, res.score),
        can_attempt: false,
      } : d);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const resultMap = {};
  (outcome?.results || data.last_attempt?.results || []).forEach((r) => { resultMap[r.question_id] = r; });
  const showResults = !!outcome || (data.passed && data.last_attempt);

  return (
    <div className="space-y-4" data-testid="assessment-panel">
      <div className="card-surface flex flex-wrap items-center justify-between gap-3 p-5">
        <div>
          <h3 className="font-heading text-[18px] font-semibold">{data.title}</h3>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Pass mark <span className="tnum font-medium">{data.pass_mark}%</span> ·{" "}
            <span className="tnum">{data.attempts_used}</span> used ·{" "}
            <span className="tnum">{data.attempts_remaining}</span> attempt(s) left
          </p>
        </div>
        {data.passed ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 text-sm font-medium text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300" data-testid="assessment-passed-badge">
            <Award className="h-4 w-4" /> Passed with {data.best_score}%
          </span>
        ) : data.best_score != null ? (
          <span className="text-sm text-muted-foreground">Best score: <span className="tnum font-semibold">{data.best_score}%</span></span>
        ) : null}
      </div>

      {!data.lessons_complete && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-700 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200" data-testid="assessment-locked-notice">
          <Lock className="h-4 w-4" /> Complete every lesson to unlock the assessment.
        </div>
      )}

      {data.passed && (
        <div className="card-surface flex flex-wrap items-center justify-between gap-3 p-5">
          <div className="flex items-center gap-3">
            <div className="grid h-11 w-11 place-items-center rounded-full bg-[#33B6E4]/12 text-[#33B6E4]"><Award className="h-5 w-5" /></div>
            <div>
              <div className="font-semibold">Certificate earned</div>
              <div className="text-sm text-muted-foreground">Passed at <span className="tnum">{data.best_score}%</span>. Your certificate is in your CPD record.</div>
            </div>
          </div>
          <Button variant="outline" onClick={() => navigate("/cpd")} data-testid="go-to-cpd-button">
            <Download className="mr-2 h-4 w-4" /> View CPD record
          </Button>
        </div>
      )}

      {data.questions.map((q, i) => (
        <QuestionCard
          key={q.id}
          q={q}
          index={i}
          value={answers[q.id]}
          onChange={(v) => setAnswers((a) => ({ ...a, [q.id]: v }))}
          disabled={showResults || !data.can_attempt}
          result={showResults ? resultMap[q.id] : null}
        />
      ))}

      <div className="flex flex-wrap items-center gap-3">
        {data.can_attempt && !outcome && (
          <Button className="btn-brand h-11" disabled={submitting} onClick={submit} data-testid="submit-assessment-button">
            {submitting ? "Submitting…" : "Submit answers"}
          </Button>
        )}
        {outcome && !outcome.passed && data.attempts_remaining > 0 && (
          <Button variant="outline" className="h-11" onClick={load} data-testid="retake-assessment-button">
            <RotateCcw className="mr-2 h-4 w-4" /> Retake assessment
          </Button>
        )}
        {outcome && (
          <div className={cn("rounded-lg px-4 py-2 text-sm font-medium",
            outcome.passed ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300"
                           : "bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-300")}
            data-testid="assessment-outcome">
            You scored {outcome.score}% ({outcome.correct_count}/{outcome.total}).{" "}
            {outcome.passed ? "Passed." : `Pass mark is ${outcome.pass_mark}%.`}
          </div>
        )}
      </div>
    </div>
  );
}
